import { z } from 'zod';
import { sapSystemSchema, siteCodeSchema } from '../sites';
import { timestampSchema } from '../timestamp';
import { notificationStatusSchema } from './enums';
import { notificationFieldsSchema } from './notification';
import { fromSap, toSap } from './sap-format';

// ! 👇 Contract with HIP EDA, over SQS. Bump `VERSION` on any breaking change. 👇
//
// Same field names as HIP: nothing to rename. Only values are converted at the boundary (./sap-format):
//   app -> HIP queue:  JSON.stringify(encodeSapNotificationRequest(request))
//   HIP queue -> app:  decodeSapInboundMessage(JSON.parse(body))

/** Every message crossing the HIP boundary says what it is in `TYPE`. */
export enum SapNotificationActionTypeEnum {
  NOTIFICATION_CREATE = 'NOTIFICATION_CREATE', // MyMaintenance -> HIP: create it in SAP
  NOTIFICATION_ACK = 'NOTIFICATION_ACK', // HIP -> MyMaintenance: SAP's answer to a create
  NOTIFICATION_CHANGED = 'NOTIFICATION_CHANGED', // HIP -> MyMaintenance: created or updated directly in SAP
}

const messageBaseSchema = z.object({ VERSION: z.literal(1) });

// App-local fields never cross the HIP boundary.
const sapNotificationFieldsSchema = notificationFieldsSchema.omit({
  PREDIAGNOSIS: true,
  REJECTION: true,
  MODIFICATION: true,
  SYNCERROR: true,
});

// ! 👇 MyMaintenance -> HIP: create this notification in SAP.
// No ID / SYNCSTATUS / LASTCHANGEDATETIME on the wire: NOTIFICATIONID is the id, the rest is internal.
const sapNotificationToCreateSchema = sapNotificationFieldsSchema.extend({
  CREATIONDATETIME: timestampSchema,
});

export const sapNotificationRequestSchema = messageBaseSchema.extend({
  TYPE: z.literal(SapNotificationActionTypeEnum.NOTIFICATION_CREATE),
  NOTIFICATIONID: z.uuid(),
  ENVIRONMENT: sapSystemSchema,
  SITE: siteCodeSchema,
  SENTDATETIME: timestampSchema,
  NOTIFICATION: sapNotificationToCreateSchema,
});

export type SapNotificationRequest = z.infer<typeof sapNotificationRequestSchema>;

const ackBaseSchema = messageBaseSchema.extend({
  TYPE: z.literal(SapNotificationActionTypeEnum.NOTIFICATION_ACK),
  NOTIFICATIONID: z.uuid(),
  SITE: siteCodeSchema,
  PROCESSEDDATETIME: timestampSchema,
});

// ! 👇 HIP -> MyMaintenance: what SAP answered to a create.
export const sapNotificationAckSchema = z.discriminatedUnion('STATUS', [
  ackBaseSchema.extend({
    STATUS: z.literal('ACCEPTED'),
    MAINTENANCENOTIFICATION: z.string(), // QMNUM
    NOTIFPROCESSINGPHASE: z.string().optional(),
    MAINTNOTIFSTATUS: notificationStatusSchema.optional(),
  }),
  ackBaseSchema.extend({
    STATUS: z.literal('REJECTED'),
    ERRORCODE: z.string().optional(),
    ERRORMESSAGE: z.string(),
  }),
]);

export type SapNotificationAck = z.infer<typeof sapNotificationAckSchema>;

// ! 👇 HIP -> MyMaintenance: a notification created or updated directly in SAP (not through the app).
export const sapNotificationChangeSchema = messageBaseSchema.extend({
  TYPE: z.literal(SapNotificationActionTypeEnum.NOTIFICATION_CHANGED),
  ENVIRONMENT: sapSystemSchema,
  NOTIFICATIONID: z.uuid().optional(),
  CHANGEDDATETIME: timestampSchema, // older messages never overwrite newer data
  NOTIFICATION: sapNotificationFieldsSchema.extend({ MAINTENANCENOTIFICATION: z.string() }), // QMNUM
});

export type SapNotificationChange = z.infer<typeof sapNotificationChangeSchema>;

export const sapInboundMessageSchema = z.union([
  sapNotificationAckSchema,
  sapNotificationChangeSchema,
]);

export type SapInboundMessage = z.infer<typeof sapInboundMessageSchema>;

/** App request -> body to put on the HIP queue. */
export const encodeSapNotificationRequest = (request: SapNotificationRequest) =>
  toSap(sapNotificationRequestSchema.parse(request));

/** Body read from the HIP queue -> validated ack / change. Throws a ZodError on a malformed message. */
export const decodeSapInboundMessage = (body: unknown): SapInboundMessage =>
  fromSap(sapInboundMessageSchema).parse(body);
